import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';

/**
 * fields: [{ name, label, type: 'text'|'number'|'textarea'|'select'|'checkbox'|'percent', options?, step?, help? }]
 * For 'percent' the stored value is a fraction (0..1); the input shows/edits the percent value.
 */
export const FormDialog = ({ open, onOpenChange, title, fields, initial, onSubmit, submitting }) => {
  const [values, setValues] = useState({});
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      const v = {};
      fields.forEach((f) => {
        let val = initial ? initial[f.name] : undefined;
        if (f.type === 'percent' && typeof val === 'number') val = +(val * 100).toFixed(4);
        v[f.name] = val ?? (f.type === 'checkbox' ? false : f.type === 'number' || f.type === 'percent' ? '' : '');
      });
      setValues(v);
      setErr(null);
    }
  }, [open, initial, fields]);

  const set = (name, val) => setValues((s) => ({ ...s, [name]: val }));

  const isVisible = (f) => !f.showIf || f.showIf(values);

  const handleSubmit = async () => {
    setErr(null);
    const payload = {};
    for (const f of fields) {
      const visible = isVisible(f);
      let val = values[f.name];
      if (!visible) {
        // Campo nascosto (condizione non soddisfatta): azzera/annulla il valore
        payload[f.name] = f.type === 'checkbox' ? false : (f.type === 'number' || f.type === 'percent') ? 0 : null;
        continue;
      }
      const required = f.required || (typeof f.requiredIf === 'function' && f.requiredIf(values));
      if (required && (val === '' || val === null || val === undefined)) {
        setErr({ message: `Campo obbligatorio: ${f.label}` });
        return;
      }
      if (f.type === 'number') val = val === '' || val === null ? 0 : parseFloat(val);
      else if (f.type === 'percent') val = val === '' || val === null ? 0 : parseFloat(val) / 100;
      else if (f.type === 'checkbox') val = !!val;
      payload[f.name] = val;
    }
    try { await onSubmit(payload); } catch (e) { setErr(e); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="form-modal">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          {fields.filter(isVisible).map((f) => (
            <div key={f.name} className="grid gap-1.5">
              {f.type !== 'checkbox' && <Label htmlFor={f.name} className="text-xs">{f.label}{(f.required || (typeof f.requiredIf === 'function' && f.requiredIf(values))) && <span className="text-destructive ml-0.5">*</span>}</Label>}
              {f.type === 'textarea' ? (
                <Textarea id={f.name} value={values[f.name] || ''} onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              ) : f.type === 'select' ? (
                <Select value={values[f.name] ?? ''} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === 'checkbox' ? (
                <div className="flex items-center gap-2">
                  <Checkbox id={f.name} checked={!!values[f.name]} onCheckedChange={(c) => set(f.name, c)} data-testid={`field-${f.name}`} />
                  <Label htmlFor={f.name} className="text-sm font-normal">{f.label}</Label>
                </div>
              ) : (
                <Input id={f.name} type={f.type === 'percent' ? 'number' : f.type} step={f.step || (f.type === 'number' || f.type === 'percent' ? 'any' : undefined)}
                  value={values[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              )}
              {f.help && <span className="text-[11px] text-muted-foreground">{f.help}</span>}
            </div>
          ))}
          {err && <div className="text-sm text-destructive">{err?.response?.data?.detail || err?.message || 'Errore durante il salvataggio'}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="form-modal-submit">{submitting ? 'Salvataggio…' : 'Salva'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
